import { Address, Cell, Dictionary } from '@ton/core';
import { TonConnectButton, useTonAddress, useTonConnectUI, useTonWallet } from '@tonconnect/ui-react';
import { useMemo, useState } from 'react';

const DEFAULT_EXTENSION_ADDRESS = 'EQCNoKMJQIVHjG0VtckLO4J72RHAwrr79P2ARl67Se7YzYqO';
const EXTENSION_ADDRESS = import.meta.env.VITE_EXTENSION_ADDRESS || DEFAULT_EXTENSION_ADDRESS;
const NETWORK = (import.meta.env.VITE_NETWORK || 'mainnet').toLowerCase();
const TONCENTER_ENDPOINT =
  import.meta.env.VITE_TONCENTER_ENDPOINT ||
  (NETWORK === 'testnet' ? 'https://testnet.toncenter.com/api/v2/jsonRPC' : 'https://toncenter.com/api/v2/jsonRPC');
const TONCENTER_API_KEY = import.meta.env.VITE_TONCENTER_API_KEY || '';

const tonviewerBase = NETWORK === 'testnet' ? 'https://testnet.tonviewer.com' : 'https://tonviewer.com';

type CheckStatus = 'idle' | 'checking' | 'authorized' | 'not-authorized' | 'not-v5' | 'error';

type RunGetMethodResponse = {
  ok?: boolean;
  result?: {
    stack?: unknown[];
  };
  error?: string;
};

function shortAddress(addr?: string) {
  if (!addr) return '-';
  return `${addr.slice(0, 6)}...${addr.slice(-6)}`;
}

function getConnectedNetwork(wallet: ReturnType<typeof useTonWallet>) {
  const chain = wallet?.account?.chain;
  if (chain === '-239') return 'mainnet';
  if (chain === '-3') return 'testnet';
  return chain || 'unknown';
}

function parseToncenterCellFromStack(stack: unknown[]): Cell | null {
  if (!Array.isArray(stack) || stack.length === 0) return null;
  const first = stack[0] as any;

  // Toncenter common shape: ["cell", {"bytes":"te6..."}]
  if (Array.isArray(first) && first[0] === 'cell') {
    const payload = first[1];
    const boc = typeof payload === 'string' ? payload : payload?.bytes || payload?.cell || payload?.value;
    if (!boc) return null;
    return Cell.fromBase64(boc);
  }

  // Some providers return { type: 'cell', cell: '...' }
  if (first && typeof first === 'object' && (first.type === 'cell' || first['@type'] === 'tvm.stackEntryCell')) {
    const boc = first.cell || first.bytes || first.value?.bytes || first.value;
    if (!boc || typeof boc !== 'string') return null;
    return Cell.fromBase64(boc);
  }

  return null;
}

function hasExtension(extensionsCell: Cell, walletAddress: Address, extensionAddress: Address) {
  const dict = Dictionary.loadDirect(Dictionary.Keys.BigUint(256), Dictionary.Values.BigInt(1), extensionsCell);
  const keys = dict.keys();
  const extensionKey = BigInt(`0x${extensionAddress.hash.toString('hex')}`);
  const sameWorkchain = walletAddress.workChain === extensionAddress.workChain;
  return sameWorkchain && keys.some((key) => key === extensionKey);
}

async function getExtensionsCell(walletAddress: string): Promise<Cell | null> {
  const response = await fetch(TONCENTER_ENDPOINT, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      ...(TONCENTER_API_KEY ? { 'X-API-Key': TONCENTER_API_KEY } : {}),
    },
    body: JSON.stringify({
      id: '1',
      jsonrpc: '2.0',
      method: 'runGetMethod',
      params: {
        address: walletAddress,
        method: 'get_extensions',
        stack: [],
      },
    }),
  });

  if (!response.ok) {
    throw new Error(`HTTP ${response.status}`);
  }

  const json = (await response.json()) as RunGetMethodResponse;
  if (json.ok === false || json.error) {
    const message = json.error || 'runGetMethod failed';
    if (/method|exit_code|exit code|unknown/i.test(message)) return null;
    throw new Error(message);
  }

  const stack = json.result?.stack;
  if (!stack) return null;
  return parseToncenterCellFromStack(stack);
}

export default function App() {
  const [tonConnectUI] = useTonConnectUI();
  const wallet = useTonWallet();
  const userFriendlyAddress = useTonAddress();
  const rawAddress = useTonAddress(false);
  const [copied, setCopied] = useState('');
  const [status, setStatus] = useState<CheckStatus>('idle');
  const [statusText, setStatusText] = useState('连接钱包后可检查授权状态。');

  const extension = useMemo(() => Address.parse(EXTENSION_ADDRESS), []);
  const extensionLink = `${tonviewerBase}/${EXTENSION_ADDRESS}`;
  const connectedNetwork = getConnectedNetwork(wallet);
  const networkMismatch = wallet && connectedNetwork !== 'unknown' && connectedNetwork !== NETWORK;

  const manualGuide = `请在支持 Wallet V5 Extension 的钱包中手动添加扩展合约地址：\n\n${EXTENSION_ADDRESS}\n\n添加后返回本页面点击“检查授权状态”。`;

  async function copy(label: string, value: string) {
    await navigator.clipboard.writeText(value);
    setCopied(label);
    setTimeout(() => setCopied(''), 1800);
  }

  async function checkAuthorization() {
    if (!rawAddress) {
      setStatus('error');
      setStatusText('请先连接钱包。');
      return;
    }

    setStatus('checking');
    setStatusText('正在查询 Wallet V5 get_extensions...');

    try {
      const walletAddress = Address.parse(rawAddress);
      const extensionsCell = await getExtensionsCell(rawAddress);
      if (!extensionsCell) {
        setStatus('not-v5');
        setStatusText('当前钱包未返回 get_extensions，可能不是 Wallet V5，或 RPC 不支持该 get 方法。');
        return;
      }

      const authorized = hasExtension(extensionsCell, walletAddress, extension);
      setStatus(authorized ? 'authorized' : 'not-authorized');
      setStatusText(
        authorized
          ? '已授权：这个 Wallet V5 已添加该扩展合约。'
          : '未授权：未在当前 Wallet V5 的 extensions 字典里找到该扩展地址。',
      );
    } catch (error) {
      setStatus('error');
      setStatusText(error instanceof Error ? error.message : '检查授权状态失败。');
    }
  }

  return (
    <main className="page">
      <section className="hero card">
        <div>
          <p className="eyebrow">TON Wallet V5</p>
          <h1>扩展授权页面</h1>
          <p className="subtitle">
            客户无需提供助记词。连接钱包后，本页面会展示扩展合约地址，并检查当前 Wallet V5 是否已经授权。
          </p>
        </div>
        <TonConnectButton />
      </section>

      <section className="grid two">
        <div className="card">
          <h2>连接的钱包</h2>
          <dl className="info">
            <dt>地址</dt>
            <dd>{userFriendlyAddress ? `${shortAddress(userFriendlyAddress)} (${userFriendlyAddress})` : '未连接'}</dd>
            <dt>网络</dt>
            <dd className={networkMismatch ? 'bad' : ''}>{connectedNetwork}</dd>
            <dt>钱包 App</dt>
            <dd>{wallet?.device?.appName || '未连接'}</dd>
          </dl>
          {networkMismatch && <p className="notice bad-bg">当前页面配置为 {NETWORK}，但钱包连接的是 {connectedNetwork}。</p>}
          <button className="secondary" onClick={() => tonConnectUI.disconnect()} disabled={!wallet}>
            断开连接
          </button>
        </div>

        <div className="card">
          <h2>扩展合约</h2>
          <p className="mono addressBox">{EXTENSION_ADDRESS}</p>
          <div className="buttonRow">
            <button onClick={() => copy('address', EXTENSION_ADDRESS)}>复制地址</button>
            <button className="secondary" onClick={() => copy('link', extensionLink)}>复制 Tonviewer 链接</button>
            <a className="linkButton" href={extensionLink} target="_blank" rel="noreferrer">打开 Tonviewer</a>
          </div>
          {copied && <p className="success">已复制：{copied}</p>}
        </div>
      </section>

      <section className="card">
        <div className="sectionTitle">
          <div>
            <h2>检查授权状态</h2>
            <p>查询当前连接钱包的 Wallet V5 `get_extensions`，判断是否包含本扩展合约地址。</p>
          </div>
          <button onClick={checkAuthorization} disabled={!wallet || status === 'checking'}>
            {status === 'checking' ? '检查中...' : '检查授权状态'}
          </button>
        </div>
        <div className={`status ${status}`}>
          <strong>{statusLabel(status)}</strong>
          <span>{statusText}</span>
        </div>
      </section>

      <section className="card warning">
        <h2>授权方式</h2>
        <p>
          当前标准 TonConnect 普通交易不能直接发起 Wallet V5 的 <code>add_extension</code> extended action。
          所以本页面不会要求助记词，也不会伪装成普通转账授权。
        </p>
        <ol>
          <li>连接钱包确认当前地址。</li>
          <li>复制扩展合约地址。</li>
          <li>在支持 Wallet V5 Extension 管理的钱包里手动添加该扩展。</li>
          <li>回到本页面点击“检查授权状态”。</li>
        </ol>
        <div className="buttonRow">
          <button onClick={() => copy('manual-guide', manualGuide)}>复制手动授权说明</button>
          <button className="secondary" onClick={() => copy('extension-address', EXTENSION_ADDRESS)}>复制扩展地址</button>
        </div>
      </section>

      <section className="card subtle">
        <h2>给客户看的说明</h2>
        <p>
          授权后，Wallet V5 会信任来自这个扩展合约地址的 extension request。这个扩展合约本身只允许固定 admin 调用。
          如果钱包不再需要授权，可以在钱包的 Wallet V5 Extension 管理界面移除此扩展地址。
        </p>
      </section>
    </main>
  );
}

function statusLabel(status: CheckStatus) {
  switch (status) {
    case 'checking': return '查询中';
    case 'authorized': return '已授权';
    case 'not-authorized': return '未授权';
    case 'not-v5': return '不是 Wallet V5 / 不支持查询';
    case 'error': return '查询失败';
    default: return '等待检查';
  }
}
